import React from "react";
import { MapPin, Home, Heart, BellRing, Eye, Clock } from "lucide-react";

interface FeatureSectionProps {
  className?: string;
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight?: string;
}

function FeatureCard({
  icon,
  title,
  description,
  highlight,
}: FeatureCardProps) {
  return (
    <div className="p-6 rounded-2xl bg-[#F4E7DA]/5 backdrop-blur-sm border border-[#F4E7DA]/10 hover:bg-[#F4E7DA]/10 transition-all duration-300 group">
      <div className="mb-4 text-burnt-orange group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-midnight-blue">{title}</h3>
      <p className="text-midnight-blue/80 mb-3 leading-relaxed">
        {description}
      </p>
      {highlight && (
        <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
          {highlight}
        </div>
      )}
    </div>
  );
}

export default function FeaturesSection({
  className = "",
}: FeatureSectionProps) {
  return (
    <section className={`px-4 py-16 ${className}`}>
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
            Những Tính Năng Thấu Hiểu Nỗi Lòng Cha Mẹ
          </h2>
          <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
            Locuno không chỉ là công nghệ, mà là sự đồng cảm. Mỗi tính năng được
            thiết kế từ chính những lo âu và mong muốn của người làm cha mẹ.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<MapPin className="w-8 h-8" />}
            title="Luôn Biết Con Ở Đâu"
            description="Dù con đang ở trường, khu vui chơi hay trên đường về nhà, bạn luôn biết con an toàn. Không còn lo lắng khi con muộn, không còn phải gọi liên tục để hỏi thăm."
            highlight="An tâm tuyệt đối"
          />
          <FeatureCard
            icon={<BellRing className="w-8 h-8" />}
            title="Cứu Trợ Tức Thì Khi Con Gặp Khó Khăn"
            description="Khi con gặp tình huống khẩn cấp, chỉ một chạm nhẹ là toàn bộ gia đình biết ngay. Bạn có thể yên tâm rằng con luôn có cách liên lạc với gia đình, dù điện thoại hết pin hay mất sóng."
            highlight="Hỗ trợ 24/7"
          />
          <FeatureCard
            icon={<Home className="w-8 h-8" />}
            title="Tạo Vòng Tay Bảo Vệ Quanh Con"
            description="Trường học, nhà bà, khu vui chơi - những nơi con thường đến sẽ trở thành những 'vòng tay an toàn'. Bạn sẽ nhận thông báo nhẹ nhàng khi con đến và rời khỏi những nơi này."
            highlight="Thông minh & tinh tế"
          />
          <FeatureCard
            icon={<Eye className="w-8 h-8" />}
            title="Phát Hiện Sớm Điều Bất Thường"
            description="Hệ thống thông minh nhận biết khi có điều gì đó không bình thường - con đi lạc đường, di chuyển quá nhanh, hoặc ở nơi xa lạ. Bạn sẽ được thông báo để có thể hành động kịp thời."
            highlight="Bảo vệ chủ động"
          />
          <FeatureCard
            icon={<Clock className="w-8 h-8" />}
            title="Ghi Nhớ Mọi Khoảnh Khắc An Toàn"
            description="Theo dõi những thói quen tốt của con: đi học đúng giờ, về nhà an toàn, chơi ở những nơi phù hợp. Một cuốn nhật ký tình yêu ghi lại hành trình trưởng thành của con."
            highlight="Kỷ niệm quý giá"
          />
          <FeatureCard
            icon={<Heart className="w-8 h-8" />}
            title="Người Bạn Thông Minh 24/7"
            description="Như một người bạn thầm lặng, hệ thống luôn canh giữ và nhắc nhở: pin điện thoại yếu, thời tiết xấu, khu vực nguy hiểm. Bạn sẽ luôn được chuẩn bị sẵn sàng để bảo vệ con."
            highlight="Thấu hiểu & tận tâm"
          />
        </div>
      </div>
    </section>
  );
}
